<template>
  <transition name="transition-fade">
    <div
      v-show="visible"
      data-dialog
      role="dialog"
      aria-modal="true"
      ref="dialog"
      tabindex="0"
      class="fixed inset-0 z-50 flex justify-center"
      :class="custom ? 'items-center' : 'items-end sm:items-center'"
    >
      <div
        data-background
        class="absolute inset-0 bg-black opacity-75"
        @click.self="handleCancel"
      />
      <transition :name="transitionName">
        <div
          data-content
          v-if="visible"
          :class="contentClassList"
        >
          <div :class="custom ? '' : 'p-4'">
            <div class="flex flex-col items-center text-center  sm:flex-row sm:items-start sm:text-left">
              <div
                v-if="!custom"
                data-icon
                class="w-8 h-8 rounded-full flex justify-center items-center flex-shrink-0"
                :class="classList"
              >
                <vue-feather :type="icon"
                             data-icon-svg
                             class="w-4 h-4"></vue-feather>
              </div>
              <div class="w-full"
                   :class="custom ? '' : 'pt-2 sm:pt-0 sm:pl-4'">
                <div v-if="heading && !custom"
                     data-heading
                     tabindex="0"
                     class="font-bold text-lg mb-2 leading-tight"
                     v-html="heading ? heading : $t('message.dialog.heading')"
                />
                <div
                  data-body
                  class="text-gray-500 leading-tight text-sm w-full"
                >
                  <slot />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!custom"
            data-footer
            class="bg-gray-50 p-3 flex flex-col sm:flex-row sm:justify-end"
          >
            <ActionButton
              v-if="hasCancelOption"
              class="btn--secondary order-2 w-auto bg-white mt-2 sm:order-1 sm:mt-0 sm:mr-2"
              data-cancel
              :label="cancelLabel ? cancelLabel : $t('message.dialog.cancelLabel')"
              @click="handleCancel"
            />
            <ActionButton
              class="order-1 w-auto sm:order-2 text-white btn--primary"
              data-confirm
              ref="confirmBtn"
              :label="confirmLabel ? confirmLabel : $t('message.dialog.confirmLabel')"
              @click="handleConfirm"
            />
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import ActionButton from '@vuedir/components/ActionButton'
import Body from '@vuedir/components/Body'
import Checkbox from '@vuedir/components/Checkbox'
import Footer from '@vuedir/components/Footer'
import Header from '@vuedir/components/Header'
import { trapTabFocus } from '@canvaspop/scoops'
import VueFeather from 'vue-feather';

/**
 * Dialog.vue
 */
export default {
  components: {
    ActionButton,
    Body,
    Checkbox,
    Footer,
    Header,
    VueFeather
  },

  props: {
    heading: {
      type: String,
      default: ''
    },

    hasCancelOption: {
      type: Boolean,
      default: false,
    },

    cancelLabel: {
      type: String,
      default: ''
    },

    confirmLabel: {
      type: String,
      default: ''
    },

    visible: {
      type: Boolean,
      default: false,
    },

    custom: {
      type: Boolean,
      default: false,
    },

    transitionName: {
      type: String,
      default: 'transition-fade-pop',
    },

    maxWidth: {
      type: String,
      default: 'max-w-md',
    },

    type: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'alert', 'positive', 'negative'].indexOf(value) !== -1,
    },
  },

  data () {
    return {
      triggerElement: null
    }
  },

  computed: {
    classList () {
      return [
        `bg-${this.shade}-50 text-${this.shade}-500`,
      ]
    },

    shade () {
      switch (this.type) {
      case 'alert':
        return 'orange'
      case 'positive':
        return 'green'
      case 'negative':
        return 'red'
      case 'info':
      default:
        return 'blue'
      }
    },

    icon () {
      switch (this.type) {
      case 'alert':
        return 'alert-circle'
      case 'positive':
        return 'thumbs-up'
      case 'negative':
        return 'thumbs-down'
      case 'info':
      default:
        return 'info'
      }
    },

    contentClassList () {
      return [
        this.custom ? 'overflow-auto' : 'overflow-hidden',
        'relative m-4 bg-white rounded shadow-lg w-full',
        this.maxWidth ? this.maxWidth : 'max-w-md',
      ]
    }
  },

  unmounted () {
    document.removeEventListener('keydown', this.handleKeyDown)
  },

  watch: {
    visible: function (isVisible) {
      if (isVisible) {
        document.body.classList.add('overflow-hidden', 'lg:overflow-hidden')
        document.body.classList.remove('lg:overflow-auto', 'lg:scrolling-touch')
        document.addEventListener('keyup', this.handleKeyUp)
        document.addEventListener('keydown', this.handleKeyDown)
        this.focusDialog()
      } else {
        document.body.classList.add('lg:overflow-auto', 'lg:scrolling-touch')
        document.body.classList.remove('overflow-hidden', 'lg:overflow-hidden')
        document.removeEventListener('keyup', this.handleKeyUp)
        document.removeEventListener('keydown', this.handleKeyDown)
        this.focusTriggerElement()
      }
    },
  },

  methods: {
    handleCancel () {
      if (this.hasCancelOption) {
        this.$emit('cancel')
      } else {
        this.$emit('confirm')
      }
    },

    handleConfirm () {
      this.$emit('confirm')
    },

    handleKeyUp (event) {
      const key = 'which' in event ? event.which : event.keyCode
      if (key === 27) {
        this.handleCancel()
      }
    },

    handleKeyDown (event) {
      const confirmBtnComponent = this.$refs.confirmBtn
      if(confirmBtnComponent) {
        trapTabFocus(this.$refs.dialog, confirmBtnComponent.$el, event)
      }
    },

    focusDialog() {
      this.triggerElement = document.activeElement
      this.$nextTick(() => {
        let dialog = this.$refs.dialog
        dialog?.focus()
      })
    },

    focusTriggerElement() {
      this.$nextTick(() => {
        this.triggerElement?.focus()
      })
    },
  },
}
</script>
